import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import FeatureCard from '../components/FeatureCard';
import ImageWithFallback from '../components/ImageWithFallback';
import ImageLightbox from '../components/ImageLightbox';
import { mediaApi } from '../services/api';
import { toYouTubeEmbed } from '../utils/youtube';

const galleryQuery = (id) => (id ? `?id=${id}` : '');

const GallerySwitcher = ({ martyrId }) => {
  const { pathname } = useLocation();
  const q = galleryQuery(martyrId);
  const tabs = [
    { to: `/gallery/photos${q}`, label: 'الصور', active: pathname.includes('/photos') },
    { to: `/gallery/videos${q}`, label: 'الفيديوهات', active: pathname.includes('/videos') }
  ];

  return (
    <div className="mb-10 flex flex-wrap justify-center gap-3">
      {tabs.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          className={`rounded-full px-6 py-2 text-sm font-semibold transition ${
            tab.active
              ? 'bg-primary-900 text-white dark:bg-gold-600'
              : 'bg-white text-stone-600 shadow-soft hover:bg-stone-100 dark:bg-stone-900 dark:text-stone-300'
          }`}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  );
};

const PhotosView = ({ martyrId }) => {
  const [photos, setPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    mediaApi.getAll({
      media_type: 'image',
      limit: 100,
      ...(martyrId && { martyr_id: martyrId })
    }).then((data) => setPhotos(data.media || []));
  }, [martyrId]);

  return (
    <>
      {photos.length === 0 && <p className="py-16 text-center text-stone-500">لا توجد صور بعد. تُضاف من لوحة الأدمن.</p>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {photos.map((photo, index) => (
          <button key={photo.id} type="button" className="group overflow-hidden rounded-2xl" onClick={() => setLightboxIndex(index)}>
            <ImageWithFallback src={photo.file_url} alt={photo.title} className="h-48 w-full object-cover transition duration-500 group-hover:scale-105" />
          </button>
        ))}
      </div>
      <ImageLightbox
        images={photos}
        index={lightboxIndex}
        onChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        renderCaption={(photo) => (
          <>
            {photo.title && <p>{photo.title}</p>}
            {photo.martyr_id && (
              <Link to={`/martyr/${photo.martyr_id}`} className="text-gold-300">{photo.martyr_name_ar}</Link>
            )}
          </>
        )}
      />
    </>
  );
};

const VideosView = ({ martyrId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const martyrVideos = await mediaApi.getAll({
          media_type: 'video',
          limit: 100,
          ...(martyrId && { martyr_id: martyrId })
        });
        const merged = (martyrVideos.media || []).map((video) => ({
          key: `media-${video.id}`,
          title: video.title,
          youtube_url: video.youtube_url,
          file_url: video.file_url,
          martyr_id: video.martyr_id,
          martyr_name_ar: video.martyr_name_ar
        }));

        if (!martyrId) {
          const response = await fetch('/api/videos?limit=100');
          const data = response.ok ? await response.json() : { videos: [] };
          (data.videos || []).forEach((video) => {
            merged.push({
              key: `site-${video.id}`,
              title: video.title,
              youtube_url: video.youtubeLink,
              file_url: null,
              martyr_id: null,
              martyr_name_ar: null
            });
          });
        }

        if (!cancelled) setItems(merged);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [martyrId]);

  if (loading) return <p className="py-16 text-center text-stone-500">جاري التحميل...</p>;
  if (items.length === 0) return <p className="py-16 text-center text-stone-500">لا توجد فيديوهات بعد.</p>;

  return (
    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
      {items.map((video) => (
        <article key={video.key} className="overflow-hidden rounded-3xl bg-black shadow-soft">
          {toYouTubeEmbed(video.youtube_url) ? (
            <iframe title={video.title} src={toYouTubeEmbed(video.youtube_url)} className="aspect-video w-full" loading="lazy" allowFullScreen />
          ) : (
            <video src={video.file_url} className="aspect-video w-full" controls preload="metadata" />
          )}
          <div className="p-4 text-white">
            <h3 className="font-bold">{video.title}</h3>
            {video.martyr_id && (
              <Link className="text-sm text-gold-300" to={`/martyr/${video.martyr_id}`}>{video.martyr_name_ar}</Link>
            )}
          </div>
        </article>
      ))}
    </div>
  );
};

const GalleryPage = () => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const martyrId = params.get('id');
  const isVideos = pathname.startsWith('/gallery/videos');
  const isHub = pathname === '/gallery' && !martyrId;

  if (isHub) {
    return (
      <Layout>
        <Seo title="المعرض" description="معرض الصور والفيديوهات في أرشيف الشهداء." />
        <PageHero eyebrow="الذاكرة البصرية" title="المعرض" subtitle="اختر الصور أو الفيديوهات. الفيديوهات تجمع كل المقاطع المضافة في الموقع." />
        <div className="container-responsive grid gap-6 py-12 md:grid-cols-2">
          <FeatureCard to="/gallery/photos" icon="▣" title="الصور" description="صور الشهداء في شبكة هادئة، مع عرض موسّع عند النقر." cta="فتح معرض الصور" />
          <FeatureCard to="/gallery/videos" icon="▶" title="الفيديوهات" description="كل فيديوهات الموقع: مقاطع الشهداء ومكتبة يوتيوب." cta="فتح معرض الفيديو" />
        </div>
      </Layout>
    );
  }

  const title = isVideos ? 'الفيديوهات' : 'الصور';
  const subtitle = isVideos
    ? (martyrId ? 'مقاطع هذا الشهيد.' : 'كل الفيديوهات المضافة في الموقع: فيديوهات الشهداء ومكتبة يوتيوب.')
    : (martyrId ? 'صور هذا الشهيد.' : 'صور الشهداء في شبكة هادئة، مع عرض موسّع عند النقر.');

  return (
    <Layout>
      <Seo title={`${title} | المعرض`} />
      <PageHero eyebrow="المعرض" title={title} subtitle={subtitle} />
      <div className="container-responsive py-12">
        <GallerySwitcher martyrId={martyrId} />
        {isVideos ? <VideosView martyrId={martyrId} /> : <PhotosView martyrId={martyrId} />}
      </div>
    </Layout>
  );
};

export default GalleryPage;
